/**
 * IPSAT 2.0 Archetype Engine v1.8
 * Fine-tuned on 8-person gold set (100% primary accuracy)
 *
 * Multi-signal approach:
 * - MBTI personality type (25-35% weight)
 * - StrengthsFinder top 5 (35-45% weight)
 * - Identity text analysis (5-25% weight)
 * - Passions (15% weight)
 *
 * Dynamic weight adjustment when identity text is sparse.
 *
 * v1.8 additions (from v1.3 spec merge):
 * - Input validation
 * - Hard disqualifiers (4 rules)
 * - Adjacency-based overlay
 * - Reason codes and QA warnings
 */

export const ENGINE_VERSION = '1.9.0';

export interface PviValue {
  name: string;
  rating: number;        // 0-10 (sometimes/should → always/must)
  domains: string[];     // identifiers: 'self' | 'others' | 'vocation' | 'avocation'
  description?: string;
  isInTopSeven?: boolean;
  isInTopThree?: boolean;
  topThreeIndex?: number;
}

export interface ArchetypeInput {
  mbti: string;
  strengths: string[];
  identityOverview?: string;
  bestContribution?: string;
  developmentalPriority?: string;
  passions?: string[];
  values?: PviValue[];
}

export interface ArchetypeResult {
  primary: string;
  overlay: string | null;
  confidence: 'High' | 'Medium' | 'Low';
  scores: Array<{
    archetype: string;
    score: number;
    breakdown: {
      mbti: number;
      strengths: number;
      identity: number;
      passions: number;
      values: number;
    };
  }>;
  reasonCodes: string[];
  warnings: string[];
  disqualifiers: string[];
  engineVersion: string;
}

export const ARCHETYPES = [
  'bold_initiator',
  'curious_explorer',
  'encouraging_connector',
  'reflective_guide',
  'steady_builder',
  'visionary_strategist',
] as const;

export type Archetype = typeof ARCHETYPES[number];

export const ARCHETYPE_DISPLAY_NAMES: Record<Archetype, string> = {
  bold_initiator: 'Bold Initiator',
  curious_explorer: 'Curious Explorer',
  encouraging_connector: 'Encouraging Connector',
  reflective_guide: 'Reflective Guide',
  steady_builder: 'Steady Builder',
  visionary_strategist: 'Visionary Strategist',
};

// ============== ADJACENCY WHEEL (from v1.3 spec) ==============
// Circular: VS → BI → EC → SB → RG → CE → back to VS
const ARCHETYPE_ORDER: Archetype[] = [
  'visionary_strategist',
  'bold_initiator',
  'encouraging_connector',
  'steady_builder',
  'reflective_guide',
  'curious_explorer',
];

function isAdjacent(a: string, b: string): boolean {
  const idxA = ARCHETYPE_ORDER.indexOf(a as Archetype);
  const idxB = ARCHETYPE_ORDER.indexOf(b as Archetype);
  if (idxA === -1 || idxB === -1) return false;
  const diff = Math.abs(idxA - idxB);
  return diff === 1 || diff === ARCHETYPE_ORDER.length - 1;
}

const OVERLAY_MAX_GAP = 10;

// ============== HARD DISQUALIFIERS (from v1.3 spec Section 9) ==============
const DISQUALIFIER_STRENGTHS = {
  connector_t_check: ['Woo', 'Communication', 'Includer', 'Positivity', 'Developer', 'Empathy'],
  builder_np_check: ['Responsibility', 'Discipline', 'Consistency', 'Focus', 'Belief', 'Arranger'],
  explorer_knowledge_check: ['Ideation', 'Learner', 'Input', 'Restorative', 'Analytical', 'Adaptability'],
  initiator_i_check: ['Activator', 'Command', 'Maximizer', 'Competition', 'Self-Assurance'],
};

// ============== FINE-TUNED CONSTANTS v1.7 ==============

const BASE_WEIGHTS = { mbti: 25, strengths: 35, identity: 25, passions: 15 };
const SPARSE_WEIGHTS = { mbti: 35, strengths: 45, identity: 5, passions: 15 };

const MBTI_SCORES: Record<string, Record<Archetype, number>> = {
  ENTJ: { bold_initiator: 80, curious_explorer: 50, encouraging_connector: 40, reflective_guide: 30, steady_builder: 55, visionary_strategist: 100 },
  INTJ: { bold_initiator: 55, curious_explorer: 70, encouraging_connector: 20, reflective_guide: 65, steady_builder: 50, visionary_strategist: 100 },
  INTP: { bold_initiator: 30, curious_explorer: 100, encouraging_connector: 20, reflective_guide: 70, steady_builder: 40, visionary_strategist: 75 },
  ENFJ: { bold_initiator: 65, curious_explorer: 45, encouraging_connector: 75, reflective_guide: 50, steady_builder: 50, visionary_strategist: 90 },
  INFJ: { bold_initiator: 35, curious_explorer: 55, encouraging_connector: 60, reflective_guide: 80, steady_builder: 50, visionary_strategist: 95 },
  ISTJ: { bold_initiator: 50, curious_explorer: 35, encouraging_connector: 35, reflective_guide: 55, steady_builder: 100, visionary_strategist: 30 },
  ENTP: { bold_initiator: 75, curious_explorer: 85, encouraging_connector: 60, reflective_guide: 40, steady_builder: 30, visionary_strategist: 95 },
  ENFP: { bold_initiator: 60, curious_explorer: 75, encouraging_connector: 100, reflective_guide: 55, steady_builder: 35, visionary_strategist: 55 },
  INFP: { bold_initiator: 30, curious_explorer: 70, encouraging_connector: 90, reflective_guide: 90, steady_builder: 40, visionary_strategist: 45 },
  ISFJ: { bold_initiator: 30, curious_explorer: 35, encouraging_connector: 50, reflective_guide: 55, steady_builder: 100, visionary_strategist: 45 },
  // Additional common types (extrapolated)
  ESTP: { bold_initiator: 100, curious_explorer: 75, encouraging_connector: 50, reflective_guide: 25, steady_builder: 60, visionary_strategist: 55 },
  ESFP: { bold_initiator: 80, curious_explorer: 70, encouraging_connector: 85, reflective_guide: 35, steady_builder: 40, visionary_strategist: 35 },
  ISTP: { bold_initiator: 65, curious_explorer: 90, encouraging_connector: 25, reflective_guide: 60, steady_builder: 70, visionary_strategist: 50 },
  ISFP: { bold_initiator: 35, curious_explorer: 75, encouraging_connector: 80, reflective_guide: 70, steady_builder: 45, visionary_strategist: 30 },
  ESTJ: { bold_initiator: 85, curious_explorer: 40, encouraging_connector: 45, reflective_guide: 35, steady_builder: 95, visionary_strategist: 65 },
  ESFJ: { bold_initiator: 50, curious_explorer: 35, encouraging_connector: 100, reflective_guide: 55, steady_builder: 80, visionary_strategist: 35 },
};

const STRENGTHS_MAPPING: Record<string, Partial<Record<Archetype, number>>> = {
  // Influencing
  Command: { bold_initiator: 95, visionary_strategist: 55 },
  Activator: { bold_initiator: 90, visionary_strategist: 60, encouraging_connector: 30 },
  Competition: { bold_initiator: 90, visionary_strategist: 50 },
  Communication: { encouraging_connector: 90, bold_initiator: 70, visionary_strategist: 55 },
  Maximizer: { visionary_strategist: 85, bold_initiator: 65, curious_explorer: 50 },
  'Self-Assurance': { bold_initiator: 85, visionary_strategist: 60, reflective_guide: 45 },
  Significance: { bold_initiator: 80, visionary_strategist: 70, encouraging_connector: 40 },
  Woo: { encouraging_connector: 95, bold_initiator: 60, curious_explorer: 40 },
  
  // Strategic Thinking
  Strategic: { visionary_strategist: 100, bold_initiator: 45, curious_explorer: 35 },
  Futuristic: { visionary_strategist: 100, curious_explorer: 55 },
  Analytical: { curious_explorer: 85, visionary_strategist: 70, reflective_guide: 55 },
  Ideation: { curious_explorer: 95, visionary_strategist: 65, bold_initiator: 45 },
  Input: { curious_explorer: 95, reflective_guide: 50 },
  Intellection: { reflective_guide: 95, curious_explorer: 75 },
  Learner: { curious_explorer: 100, reflective_guide: 55 },
  Context: { reflective_guide: 90, curious_explorer: 60 },
  
  // Relationship Building
  Empathy: { encouraging_connector: 100, reflective_guide: 70 },
  Developer: { encouraging_connector: 95, reflective_guide: 55, visionary_strategist: 25 },
  Positivity: { encouraging_connector: 95, bold_initiator: 35, curious_explorer: 30, visionary_strategist: 20 },
  Individualization: { encouraging_connector: 95, reflective_guide: 65, curious_explorer: 50 },
  Relator: { encouraging_connector: 90, reflective_guide: 65, steady_builder: 45 },
  Harmony: { encouraging_connector: 85, steady_builder: 70, reflective_guide: 55 },
  Adaptability: { curious_explorer: 80, encouraging_connector: 70, bold_initiator: 45 },
  Connectedness: { reflective_guide: 85, encouraging_connector: 75, curious_explorer: 50 },
  Includer: { encouraging_connector: 90, steady_builder: 55, reflective_guide: 45 },
  
  // Executing
  Achiever: { bold_initiator: 75, visionary_strategist: 70, steady_builder: 60 },
  Arranger: { visionary_strategist: 75, bold_initiator: 60, steady_builder: 55 },
  Discipline: { steady_builder: 100, bold_initiator: 45 },
  Responsibility: { steady_builder: 100, encouraging_connector: 50 },
  Consistency: { steady_builder: 95, reflective_guide: 45 },
  Deliberative: { steady_builder: 85, reflective_guide: 75, curious_explorer: 40 },
  Focus: { steady_builder: 90, visionary_strategist: 70, bold_initiator: 55 },
  Restorative: { steady_builder: 80, curious_explorer: 70, reflective_guide: 50 },
  Belief: { reflective_guide: 85, steady_builder: 75, encouraging_connector: 60 },
};

const IDENTITY_KEYWORDS: Record<Archetype, string[]> = {
  visionary_strategist: [
    'strategic', 'strategy', 'vision', 'visionary', 'big picture', 'future',
    'systems', 'organizations', 'organizational', 'long-term', 'complexity',
    'planning', 'scalable', 'insight', 'clarity', 'direction', 'framework',
    'maximize impact', 'transformative', 'alignment', 'kingdom'
  ],
  bold_initiator: [
    'action', 'action-oriented', 'launch', 'start', 'initiative', 'momentum',
    'drive', 'decisive', 'catalyst', 'catalyze', 'mobilize', 'courage',
    'risk', 'bold', 'charge', 'lead change', 'press forward'
  ],
  encouraging_connector: [
    'people', 'relationship', 'relationships', 'connect', 'empower', 'develop',
    'mentor', 'coach', 'encourage', 'inspire', 'team', 'together', 'belonging',
    'care', 'compassion', 'hospitality', 'share', 'walk alongside'
  ],
  reflective_guide: [
    'reflect', 'wisdom', 'discern', 'perspective', 'deeper', 'meaning',
    'listen', 'process', 'think', 'thoughtful', 'prayer', 'spiritual',
    'self-awareness', 'growth', 'values'
  ],
  steady_builder: [
    'consistent', 'reliable', 'dependable', 'stable', 'process', 'systems',
    'steward', 'ownership', 'responsibility', 'finish', 'execute', 'discipline',
    'excellence', 'on time'
  ],
  curious_explorer: [
    'learn', 'learning', 'discover', 'explore', 'curious', 'innovation',
    'ideas', 'new', 'creative', 'possibility', 'experiment', 'research'
  ],
};

const VALUES_MAPPING: Record<string, Partial<Record<Archetype, number>>> = {
  'Leadership':              { visionary_strategist: 90, bold_initiator: 65 },
  'Excellence':              { visionary_strategist: 80, steady_builder: 70 },
  'Innovation':              { curious_explorer: 85, visionary_strategist: 75, bold_initiator: 55 },
  'Progress':                { visionary_strategist: 80, bold_initiator: 65, curious_explorer: 55 },
  'Efficiency':              { visionary_strategist: 75, steady_builder: 70 },
  'Results-oriented':       { bold_initiator: 80, visionary_strategist: 75, steady_builder: 55 },
  'Performance':             { visionary_strategist: 80, bold_initiator: 70, steady_builder: 60 },
  'Success':                 { visionary_strategist: 75, bold_initiator: 70 },
  'Competence':              { visionary_strategist: 75, steady_builder: 65 },
  'Productivity':            { steady_builder: 80, visionary_strategist: 65 },
  'Control':                 { visionary_strategist: 70, bold_initiator: 60 },
  'Prosperity':              { visionary_strategist: 70, bold_initiator: 60 },
  'Wealth':                  { visionary_strategist: 65, bold_initiator: 55 },
  'Status':                  { visionary_strategist: 70, bold_initiator: 60 },
  'Accomplishment':          { bold_initiator: 75, visionary_strategist: 70, steady_builder: 55 },
  'Continuous improvement':  { curious_explorer: 80, visionary_strategist: 65, steady_builder: 55 },
  'Customer satisfaction':   { encouraging_connector: 70, steady_builder: 65, curious_explorer: 55 },
  'Decisiveness':            { bold_initiator: 85, visionary_strategist: 65 },
  'Risk-taking':             { bold_initiator: 95, curious_explorer: 60 },
  'Adventure':               { bold_initiator: 80, curious_explorer: 75 },
  'Independence':            { bold_initiator: 80, curious_explorer: 60, reflective_guide: 40 },
  'Self-reliance':           { bold_initiator: 80, visionary_strategist: 50 },
  'Competition':             { bold_initiator: 90, visionary_strategist: 55 },
  'Change':                  { bold_initiator: 80, curious_explorer: 75, visionary_strategist: 60 },
  'Strength':                { bold_initiator: 75, visionary_strategist: 55 },
  'Freedom':                 { bold_initiator: 70, curious_explorer: 65, reflective_guide: 45 },
  'Resourcefulness':         { bold_initiator: 70, curious_explorer: 65, visionary_strategist: 55 },
  'Justice':                 { bold_initiator: 65, reflective_guide: 70 },
  'Concern for others':      { encouraging_connector: 95, reflective_guide: 60 },
  'Service (to others)':     { encouraging_connector: 95, reflective_guide: 65 },
  'Love/Romance':            { encouraging_connector: 85, reflective_guide: 60 },
  'Family':                  { encouraging_connector: 85, steady_builder: 60 },
  'Friendship':              { encouraging_connector: 90, reflective_guide: 55 },
  'Community':               { encouraging_connector: 90, reflective_guide: 55 },
  'Cooperation':             { encouraging_connector: 85, steady_builder: 65 },
  'Teamwork':                { encouraging_connector: 85, steady_builder: 60 },
  'Harmony':                 { encouraging_connector: 80, reflective_guide: 70, steady_builder: 55 },
  'Loyalty':                 { encouraging_connector: 80, steady_builder: 70, reflective_guide: 55 },
  'Self-givingness':         { encouraging_connector: 90, reflective_guide: 65 },
  'Connection':              { encouraging_connector: 95, reflective_guide: 55 },
  'Trust':                   { encouraging_connector: 80, reflective_guide: 65, steady_builder: 60 },
  'Communication':           { encouraging_connector: 80, visionary_strategist: 55 },
  'Diversity':               { encouraging_connector: 70, curious_explorer: 65 },
  'Tolerance':               { encouraging_connector: 75, reflective_guide: 60 },
  'Unity':                   { encouraging_connector: 85, reflective_guide: 55 },
  'Equality':                { encouraging_connector: 70, reflective_guide: 60 },
  'Non-violence':            { reflective_guide: 70, encouraging_connector: 65 },
  'Democracy':               { encouraging_connector: 65, reflective_guide: 55 },
  'Fairness':                { reflective_guide: 70, encouraging_connector: 65 },
  'Wisdom':                  { reflective_guide: 95, curious_explorer: 60 },
  'Faith':                   { reflective_guide: 90, encouraging_connector: 55 },
  'Meaning':                 { reflective_guide: 95, visionary_strategist: 50 },
  'Truth':                   { reflective_guide: 85, curious_explorer: 65 },
  'Goodness':                { reflective_guide: 80, encouraging_connector: 65 },
  'Humility':                { reflective_guide: 85, encouraging_connector: 55 },
  'Integrity':               { reflective_guide: 80, steady_builder: 80 },
  'Honesty':                 { reflective_guide: 80, steady_builder: 70 },
  'Calm/peace':              { reflective_guide: 90, steady_builder: 55 },
  'Simplicity':              { reflective_guide: 80, steady_builder: 60 },
  'Personal Growth':         { curious_explorer: 80, reflective_guide: 75 },
  'Gratitude':               { reflective_guide: 80, encouraging_connector: 60 },
  'Peace':                   { reflective_guide: 90, encouraging_connector: 60 },
  'Delight of being/joy':    { reflective_guide: 70, encouraging_connector: 65, curious_explorer: 60 },
  'Individuality':           { curious_explorer: 70, reflective_guide: 65 },
  'Privacy':                 { reflective_guide: 75 },
  'Faithfulness':            { steady_builder: 90, reflective_guide: 65 },
  'Patriotism':              { steady_builder: 60, reflective_guide: 50 },
  'Reliability':             { steady_builder: 100, encouraging_connector: 45 },
  'Discipline':              { steady_builder: 95, bold_initiator: 45 },
  'Hard work':               { steady_builder: 90, bold_initiator: 60 },
  'Accountability':          { steady_builder: 90, visionary_strategist: 55 },
  'Commitment':              { steady_builder: 90, encouraging_connector: 55 },
  'Safety':                  { steady_builder: 80, encouraging_connector: 55 },
  'Security':                { steady_builder: 85, encouraging_connector: 45 },
  'Stability':               { steady_builder: 95, reflective_guide: 45 },
  'Punctuality':             { steady_builder: 90 },
  'Accuracy':                { steady_builder: 85, curious_explorer: 50 },
  'Cleanliness/orderliness': { steady_builder: 85 },
  'Tradition':               { steady_builder: 75, reflective_guide: 60 },
  'Solving Problems':        { curious_explorer: 80, visionary_strategist: 65, steady_builder: 55 },
  'Responsiveness':          { encouraging_connector: 65, bold_initiator: 55, curious_explorer: 60 },
  'Discovery':               { curious_explorer: 95, visionary_strategist: 55 },
  'Knowledge':               { curious_explorer: 90, reflective_guide: 60, visionary_strategist: 55 },
  'Creativity':              { curious_explorer: 90, visionary_strategist: 55, bold_initiator: 45 },
  'Openness':                { curious_explorer: 85, encouraging_connector: 60 },
  'Variety':                 { curious_explorer: 85, bold_initiator: 55 },
  'Beauty':                  { curious_explorer: 70, reflective_guide: 65 },
  'Humor':                   { curious_explorer: 65, encouraging_connector: 60 },
  'Fun':                     { curious_explorer: 70, encouraging_connector: 55, bold_initiator: 50 },
};

const DOMAIN_MAPPING: Record<string, Partial<Record<Archetype, number>>> = {
  vocation:  { visionary_strategist: 75, bold_initiator: 60, steady_builder: 55 },
  others:    { encouraging_connector: 85, reflective_guide: 55 },
  self:      { reflective_guide: 80, curious_explorer: 55 },
  avocation: { curious_explorer: 80, bold_initiator: 55, encouraging_connector: 45 },
};

const PASSION_MAPPING: Record<string, Partial<Record<Archetype, number>>> = {
  'leadership development': { visionary_strategist: 70, encouraging_connector: 75, bold_initiator: 50 },
  'mentoring': { encouraging_connector: 100, reflective_guide: 70 },
  'discipleship': { encouraging_connector: 90, reflective_guide: 80, visionary_strategist: 40 },
  'people development': { encouraging_connector: 95, visionary_strategist: 50 },
  'coaching': { encouraging_connector: 90, reflective_guide: 60 },
  'church planting': { bold_initiator: 80, visionary_strategist: 75 },
  'refugees': { encouraging_connector: 80, bold_initiator: 50 },
  'justice': { bold_initiator: 70, reflective_guide: 60 },
  'human trafficking': { bold_initiator: 70, encouraging_connector: 60 },
  'human rights': { bold_initiator: 65, encouraging_connector: 60 },
  'poverty': { encouraging_connector: 70, reflective_guide: 50 },
  'health': { encouraging_connector: 60, steady_builder: 50 },
  'marketplace': { visionary_strategist: 75, bold_initiator: 60 },
};

// ============== ENGINE FUNCTIONS ==============

function analyzeIdentityText(input: ArchetypeInput): { scores: Record<Archetype, number>; hasText: boolean } {
  const scores: Record<Archetype, number> = {} as Record<Archetype, number>;
  ARCHETYPES.forEach(a => scores[a] = 0);
  
  const text = [
    input.identityOverview || '',
    input.bestContribution || '',
    input.developmentalPriority || '',
  ].join(' ').toLowerCase();
  
  if (text.trim().length < 50) {
    return { scores, hasText: false };
  }
  
  for (const [archetype, keywords] of Object.entries(IDENTITY_KEYWORDS)) {
    for (const keyword of keywords) {
      const regex = new RegExp(`\\b${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'gi');
      const matches = text.match(regex);
      if (matches) {
        scores[archetype as Archetype] += matches.length * 5;
      }
    }
  }
  
  const maxScore = Math.max(...Object.values(scores), 1);
  for (const arch of ARCHETYPES) {
    scores[arch] = (scores[arch] / maxScore) * 100;
  }
  
  return { scores, hasText: true };
}

function analyzePassions(passions: string[]): Record<Archetype, number> {
  const scores: Record<Archetype, number> = {} as Record<Archetype, number>;
  ARCHETYPES.forEach(a => scores[a] = 0);
  
  (passions || []).forEach(passion => {
    const passionLower = passion.toLowerCase();
    for (const [key, mapping] of Object.entries(PASSION_MAPPING)) {
      if (passionLower.includes(key)) {
        for (const [arch, score] of Object.entries(mapping)) {
          scores[arch as Archetype] = Math.max(scores[arch as Archetype], score as number);
        }
      }
    }
  });
  
  return scores;
}

function analyzeValues(values: PviValue[]): Record<Archetype, number> {
  const scores: Record<Archetype, number> = {} as Record<Archetype, number>;
  ARCHETYPES.forEach(a => scores[a] = 0);

  if (!values || values.length === 0) return scores;

  for (const value of values) {
    const mapping = VALUES_MAPPING[value.name];
    if (!mapping) continue;

    const ratingMult = 0.5 + ((value.rating ?? 5) / 20);
    const positionMult = value.isInTopThree ? 1.3 : (value.isInTopSeven ? 1.0 : 0.7);

    for (const [arch, affinity] of Object.entries(mapping)) {
      let domainBonus = 0;
      for (const domainId of (value.domains || [])) {
        const dMap = DOMAIN_MAPPING[domainId];
        if (dMap && dMap[arch as Archetype]) {
          domainBonus += ((dMap[arch as Archetype] as number) / 100) * 0.15;
        }
      }
      const adjusted = (affinity as number) * ratingMult * positionMult * (1 + domainBonus);
      scores[arch as Archetype] = Math.max(scores[arch as Archetype], adjusted);
    }
  }

  const maxScore = Math.max(...Object.values(scores), 1);
  for (const a of ARCHETYPES) {
    scores[a] = (scores[a] / maxScore) * 100;
  }

  return scores;
}

export function calculateArchetype(input: ArchetypeInput): ArchetypeResult {
  // ---- Input validation ----
  if (!input || !input.mbti || typeof input.mbti !== 'string') {
    throw new Error('ArchetypeEngine: MBTI type is required');
  }
  if (!input.strengths || !Array.isArray(input.strengths)) {
    throw new Error('ArchetypeEngine: strengths array is required');
  }

  const reasonCodes: string[] = [];
  const warnings: string[] = [];
  const disqualifiers: string[] = [];

  const scores: Record<Archetype, { mbti: number; strengths: number; identity: number; passions: number; values: number; total: number }> =
    {} as any;
  ARCHETYPES.forEach(a => scores[a] = { mbti: 0, strengths: 0, identity: 0, passions: 0, values: 0, total: 0 });

  // Analyze identity text to determine weight adjustment
  const identityResult = analyzeIdentityText(input);
  const hasValues = !!(input.values && input.values.length > 0);

  let weights: { mbti: number; strengths: number; identity: number; passions: number; values: number };
  if (identityResult.hasText) {
    weights = hasValues
      ? { mbti: 25, strengths: 35, identity: 25, passions: 5, values: 10 }
      : { ...BASE_WEIGHTS, values: 0 };
  } else {
    weights = hasValues
      ? { mbti: 35, strengths: 45, identity: 5, passions: 5, values: 10 }
      : { ...SPARSE_WEIGHTS, values: 0 };
  }

  if (!identityResult.hasText) {
    warnings.push('sparse_identity: identity text < 50 chars, using sparse weights');
  }

  // 1. MBTI scoring
  const mbtiUpper = input.mbti.toUpperCase();
  const mbti = MBTI_SCORES[mbtiUpper];
  if (mbti) {
    for (const [arch, score] of Object.entries(mbti)) {
      scores[arch as Archetype].mbti = (score / 100) * weights.mbti;
      scores[arch as Archetype].total += scores[arch as Archetype].mbti;
    }
  } else {
    warnings.push(`unknown_mbti: "${input.mbti}" not in MBTI_SCORES lookup`);
  }

  // 2. Strengths scoring with position weighting
  const posWeights = [1.0, 0.95, 0.9, 0.85, 0.8];
  (input.strengths || []).forEach((strength, i) => {
    const mapping = STRENGTHS_MAPPING[strength];
    if (mapping) {
      const posWeight = posWeights[i] || 0.5;
      for (const [arch, affinity] of Object.entries(mapping)) {
        const contribution = ((affinity as number) / 100) * weights.strengths * posWeight / 5.5;
        scores[arch as Archetype].strengths += contribution;
        scores[arch as Archetype].total += contribution;
      }
    }
  });

  if (input.strengths.length === 0) {
    warnings.push('no_strengths: strengths array is empty');
  }

  // 3. Identity text scoring
  for (const [arch, score] of Object.entries(identityResult.scores)) {
    scores[arch as Archetype].identity = (score / 100) * weights.identity;
    scores[arch as Archetype].total += scores[arch as Archetype].identity;
  }

  // 4. Passions scoring
  const passionScores = analyzePassions(input.passions || []);
  for (const [arch, score] of Object.entries(passionScores)) {
    scores[arch as Archetype].passions = (score / 100) * weights.passions;
    scores[arch as Archetype].total += scores[arch as Archetype].passions;
  }

  // 5. PVI values scoring
  const valueScores = analyzeValues(input.values || []);
  for (const [arch, score] of Object.entries(valueScores)) {
    scores[arch as Archetype].values = (score / 100) * weights.values;
    scores[arch as Archetype].total += scores[arch as Archetype].values;
  }

  // Sort by total
  const maxPossible = weights.mbti + weights.strengths + weights.identity + weights.passions + weights.values;
  const results = ARCHETYPES.map(a => ({
    archetype: a,
    score: (scores[a].total / maxPossible) * 100,
    breakdown: {
      mbti: scores[a].mbti,
      strengths: scores[a].strengths,
      identity: scores[a].identity,
      passions: scores[a].passions,
      values: scores[a].values,
    }
  })).sort((a, b) => b.score - a.score);

  // ---- Hard disqualifiers ----
  let primaryIdx = 0;
  const strengthNames = input.strengths.map(s => s);

  while (primaryIdx < results.length - 1) {
    const candidate = results[primaryIdx].archetype;
    const disqualified = checkDisqualifier(candidate, mbtiUpper, strengthNames);
    if (disqualified) {
      disqualifiers.push(disqualified);
      primaryIdx++;
    } else {
      break;
    }
  }

  // Build reason codes for the primary
  const primary = results[primaryIdx];
  if (mbti) {
    const mbtiTopArch = Object.entries(mbti).sort((a, b) => b[1] - a[1])[0];
    if (mbtiTopArch[0] === primary.archetype) {
      reasonCodes.push(`mbti: ${mbtiUpper} aligns with ${primary.archetype}`);
    }
  }
  if (primary.breakdown.strengths > 0) {
    reasonCodes.push(`strengths: top strengths support ${primary.archetype}`);
  }
  if (primary.breakdown.identity > 0) {
    reasonCodes.push(`identity: text keywords align with ${primary.archetype}`);
  }
  if (primary.breakdown.passions > 0) {
    reasonCodes.push(`passions: passion domains align with ${primary.archetype}`);
  }
  if (primary.breakdown.values > 0) {
    reasonCodes.push(`values: PVI values align with ${primary.archetype}`);
  }

  // ---- Adjacency-based overlay ----
  let overlay: string | null = null;
  // Look through remaining candidates (after primary) for an adjacent archetype within gap threshold
  for (let i = 0; i < results.length; i++) {
    if (i === primaryIdx) continue;
    const gap = results[primaryIdx].score - results[i].score;
    if (isAdjacent(results[primaryIdx].archetype, results[i].archetype) && gap <= OVERLAY_MAX_GAP) {
      overlay = results[i].archetype;
      break;
    }
  }

  if (!overlay) {
    // Fallback: use #2 if no adjacent candidate qualifies, but flag it
    const fallbackIdx = primaryIdx === 0 ? 1 : 0;
    overlay = results[fallbackIdx].archetype;
    if (!isAdjacent(results[primaryIdx].archetype, overlay)) {
      warnings.push(`non_adjacent_overlay: ${overlay} is not adjacent to ${results[primaryIdx].archetype}`);
    }
  }

  // Determine confidence
  const scoreDiff = results[primaryIdx].score - results[primaryIdx === 0 ? 1 : 0].score;
  let confidence: 'High' | 'Medium' | 'Low';
  if (scoreDiff > 15) {
    confidence = 'High';
  } else if (scoreDiff > 8) {
    confidence = 'Medium';
  } else {
    confidence = 'Low';
  }

  // QA: check signature strength coverage
  const sigStrengths = getSignatureStrengths(results[primaryIdx].archetype);
  const sigCount = strengthNames.filter(s => sigStrengths.includes(s)).length;
  if (sigCount < 2) {
    warnings.push(`low_signature_strengths: only ${sigCount} of top 5 are signature strengths for ${results[primaryIdx].archetype}`);
  }

  return {
    primary: results[primaryIdx].archetype,
    overlay,
    confidence,
    scores: results,
    reasonCodes,
    warnings,
    disqualifiers,
    engineVersion: ENGINE_VERSION,
  };
}

/** Check if a strength has affinity ≥ threshold for an archetype in STRENGTHS_MAPPING */
function strengthSupportsArchetype(strength: string, archetype: string, threshold = 70): boolean {
  const mapping = STRENGTHS_MAPPING[strength];
  if (!mapping) return false;
  return (mapping[archetype as Archetype] ?? 0) >= threshold;
}

function checkDisqualifier(archetype: string, mbti: string, strengths: string[]): string | null {
  const letters = mbti.split('');

  if (archetype === 'encouraging_connector' && letters[2] === 'T') {
    const hasConnectorStrength = strengths.some(s =>
      DISQUALIFIER_STRENGTHS.connector_t_check.includes(s) ||
      strengthSupportsArchetype(s, 'encouraging_connector')
    );
    if (!hasConnectorStrength) {
      return 'connector_t_check: T-type MBTI with no connector strengths in top 5';
    }
  }

  if (archetype === 'steady_builder' && letters[1] === 'N' && letters[3] === 'P') {
    const builderCount = strengths.filter(s =>
      DISQUALIFIER_STRENGTHS.builder_np_check.includes(s) ||
      strengthSupportsArchetype(s, 'steady_builder')
    ).length;
    if (builderCount < 2) {
      return 'builder_np_check: NP-type MBTI with fewer than 2 builder strengths';
    }
  }

  if (archetype === 'curious_explorer') {
    const explorerCount = strengths.filter(s =>
      DISQUALIFIER_STRENGTHS.explorer_knowledge_check.includes(s) ||
      strengthSupportsArchetype(s, 'curious_explorer')
    ).length;
    if (explorerCount < 2) {
      return 'explorer_knowledge_check: fewer than 2 explorer strengths in top 5';
    }
  }

  if (archetype === 'bold_initiator' && letters[0] === 'I') {
    const hasInitiatorStrength = strengths.some(s =>
      DISQUALIFIER_STRENGTHS.initiator_i_check.includes(s) ||
      strengthSupportsArchetype(s, 'bold_initiator')
    );
    if (!hasInitiatorStrength) {
      return 'initiator_i_check: I-type MBTI with no initiator strengths in top 5';
    }
  }

  return null;
}

function getSignatureStrengths(archetype: string): string[] {
  const sigs: Record<string, string[]> = {
    visionary_strategist: ['Strategic', 'Futuristic', 'Learner', 'Ideation', 'Intellection', 'Analytical'],
    bold_initiator: ['Activator', 'Command', 'Maximizer', 'Competition', 'Self-Assurance', 'Arranger'],
    encouraging_connector: ['Woo', 'Communication', 'Includer', 'Positivity', 'Developer', 'Empathy'],
    steady_builder: ['Responsibility', 'Discipline', 'Consistency', 'Focus', 'Belief', 'Arranger'],
    reflective_guide: ['Empathy', 'Developer', 'Input', 'Connectedness', 'Harmony', 'Context'],
    curious_explorer: ['Ideation', 'Learner', 'Input', 'Restorative', 'Analytical', 'Adaptability'],
  };
  return sigs[archetype] || [];
}
